import React, { useState } from 'react';
import {
    Box, Typography, IconButton, TextField, Button, Chip,
    Tooltip, Badge, Collapse, Rating,
} from '@mui/material';
import { ThumbUp, ThumbDown, Comment, Send } from '@mui/icons-material';
import ModalForms from '../Shared/Modals/index.js';

// ── Types ─────────────────────────────────────────────────────────────────────
export interface FeedbackRecord {
    queryId: string;
    rating: number | null;
    thumbs: 'up' | 'down' | null;
    comment: string;
    submitted: boolean;
}

export interface FeedbackSectionProps {
    queryId: string;
    feedback: FeedbackRecord;
    onChange: (changes: Partial<FeedbackRecord>) => void;
    onSubmit: () => void;
    forceOpen?: boolean;
    onClose?: () => void;
}

// ── FeedbackSection Component ─────────────────────────────────────────────────
const FeedbackSection: React.FC<FeedbackSectionProps> = ({
    queryId,
    feedback,
    onChange,
    onSubmit,
    onClose,
    forceOpen,
}) => {
    const [open, setOpen] = useState(false);


    return (
        <ModalForms
            show={forceOpen || open}
            handleClose={onClose}
            modalHeader={'Feedback'}
            size="md"
            modalBody={
                <Box sx={{ mt: 2, borderTop: '1px solid #f3f4f6', pt: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontWeight: 800,
                                color: '#374151',
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                            }}
                        >
                            Feedback
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Tooltip title="Helpful">
                                <IconButton
                                    size="small"
                                    onClick={() => onChange({ thumbs: feedback.thumbs === 'up' ? null : 'up' })}
                                    sx={{
                                        color: feedback.thumbs === 'up' ? '#16a34a' : '#9ca3af',
                                        '&:hover': { color: '#16a34a' },
                                    }}
                                >
                                    <ThumbUp sx={{ fontSize: 15 }} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Not helpful">
                                <IconButton
                                    size="small"
                                    onClick={() => onChange({ thumbs: feedback.thumbs === 'down' ? null : 'down' })}
                                    sx={{
                                        color: feedback.thumbs === 'down' ? '#dc2626' : '#9ca3af',
                                        '&:hover': { color: '#dc2626' },
                                    }}
                                >
                                    <ThumbDown sx={{ fontSize: 15 }} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Add comment">
                                <IconButton
                                    size="small"
                                    onClick={() => setOpen(!open)}
                                    sx={{
                                        color: (forceOpen || open) ? '#7C3AED' : '#9ca3af',
                                        '&:hover': { color: '#7C3AED' },
                                    }}
                                >
                                    <Badge badgeContent={feedback.comment ? 1 : 0} color="primary" variant="dot">
                                        <Comment sx={{ fontSize: 15 }} />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    <Collapse in={forceOpen || open}>
                        <Box sx={{ mt: 1 }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
                                Rate this response
                            </Typography>
                            <Rating
                                size="small"
                                value={feedback.rating}
                                onChange={(_, v) => onChange({ rating: v })}
                                sx={{ mb: 1, '& .MuiRating-iconFilled': { color: '#7C3AED' } }}
                            />
                            <TextField
                                multiline
                                rows={2}
                                fullWidth
                                placeholder="Leave a comment about this response…"
                                value={feedback.comment}
                                onChange={(e) => onChange({ comment: e.target.value })}
                                variant="outlined"
                                size="small"
                                disabled={feedback.submitted}
                                sx={{
                                    mb: 1,
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: 12,
                                        borderRadius: '8px',
                                        bgcolor: '#f8fafc',
                                    },
                                }}
                            />
                            {!feedback.submitted ? (
                                <Button
                                    size="small"
                                    variant="contained"
                                    endIcon={<Send sx={{ fontSize: 12 }} />}
                                    onClick={onSubmit}
                                    disabled={!feedback.thumbs && !feedback.rating && !feedback.comment.trim()}
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        borderRadius: '8px',
                                        bgcolor: '#7C3AED',
                                        '&:hover': { bgcolor: '#6D28D9' },
                                        textTransform: 'none',
                                    }}
                                >
                                    Submit Feedback
                                </Button>
                            ) : (
                                <Chip
                                    label="✓ Feedback submitted"
                                    size="small"
                                    sx={{
                                        bgcolor: '#dcfce7',
                                        color: '#14532d',
                                        fontWeight: 700,
                                        fontSize: 11,
                                    }}
                                />
                            )}
                        </Box>
                    </Collapse>
                </Box>
            }
        />
    );
};

export default FeedbackSection;